import { createContext, useContext, useEffect, useMemo, useState } from "react";
import axios, { AxiosResponse } from "axios";

import { baseUrl } from "../config";
import { Role } from "../utils/roles";
import { UserDetails } from "../utils/types";
import { TokenResponse } from "@react-oauth/google";
import useLocalStorage from "../hooks/useLocalStorage";
import { isAccessedFromQBurstApps } from "../utils/helpers";

interface UserLoginResponse {
  isLoggedIn: boolean;
  errorMessage?: string;
  hasPrimary?: boolean;
}

interface GoogleAuthContextType {
  user: UserDetails | null;
  accessToken: string | null;
  guestToken: string | null;
  userRoles: Role[];
  hasCeoRolesAccess: boolean;
  isQBurstAppsLoggingIn: boolean;
  unregisteredUserDialogOpen: boolean;
  unregisteredUserRedirectUrl: string | null;
  handleUnregisteredUserDialogClose: () => void;
  setToken: (accessToken: string) => void;
  clearUser: () => void;
  setUser: (user: UserDetails) => void;
  userLogin: (tokenResponse: TokenResponse) => Promise<UserLoginResponse>;
}

const GoogleAuthContext = createContext<GoogleAuthContextType | null>(null);

// Custom hook to access the authentication context
export const useGoogleAuth = () => {
  const context = useContext(GoogleAuthContext);
  if (!context) {
    throw new Error(
      "useGoogleAuth must be used within a GoogleUserAuthProvider",
    );
  }
  return context;
};

export const GoogleUserAuthProvider = ({ children }: { children: any }) => {
  // initially check for localstorage data to set token and user states
  const [localUserData, setLocalUserData] = useLocalStorage<UserDetails | null>(
    "userDetails",
    null,
  );
  const [, setLocalGuestToken] = useLocalStorage<string | null>(
    "guestToken",
    null,
  );

  const [guestToken, setGuestToken] = useState<string | null>(null);

  const [accessToken, setAccessToken] = useState(() =>
    localUserData ? localUserData.access_token : null,
  );

  const [isQBurstAppsLoggingIn, setIsQBurstAppsLoggingIn] = useState(false);
  const [unregisteredUserDialogOpen, setUnregisteredUserDialogOpen] =
    useState(false);
  const [unregisteredUserRedirectUrl, setUnregisteredUserRedirectUrl] =
    useState<string | null>(null);

  const setToken = (newAccessToken: string) => {
    setAccessToken(newAccessToken);
  };

  const clearUser = () => {
    setAccessToken(null);
    localStorage.clear();
  };

  const handleQBurstAppsLoginSuccess = (
    responseData: any,
  ): UserLoginResponse => {
    console.log("QBurst Apps login successful");
    setToken(responseData.access);

    const userDetails: UserDetails = {
      access_token: responseData.access,
      refresh_token: responseData.refresh ?? "",
      email: responseData.email ?? "",
      name: responseData.name ?? "",
      profile_pic: responseData.profile_image ?? "",
      id: responseData.id ?? 0,
      roles: responseData.roles ?? [],
      has_primary: responseData.has_primary_dashboard ?? false,
      virtual_team: responseData.virtual_team ?? "",
      has_ceo_roles_access: responseData.has_ceo_roles_access ?? false,
    };
    setLocalUserData(userDetails);

    return {
      isLoggedIn: true,
      hasPrimary: responseData.has_primary_dashboard,
    };
  };

  const waitBeforeRetry = async (retryCount: number): Promise<void> => {
    await new Promise((resolve) => setTimeout(resolve, 1000 * retryCount));
  };

  const attemptQBurstAppsLogin = async (): Promise<AxiosResponse> => {
    return await axios({
      method: "post",
      url: `${baseUrl}/qburst-apps/login`,
      headers: {
        "Content-Type": "application/json",
      },
      withCredentials: true,
    });
  };

  const processQBurstAppsLoginResponse = (
    response: AxiosResponse,
  ): UserLoginResponse | null => {
    if (response.status !== 200) {
      return {
        isLoggedIn: false,
        errorMessage: "Unexpected response from QBurst Apps login.",
      };
    }

    if (response.data?.message === "Qburst Apps authentication failed.") {
      return null;
    }

    if (response.data?.access) {
      return handleQBurstAppsLoginSuccess(response.data);
    }

    return {
      isLoggedIn: false,
      errorMessage: "Unexpected response from QBurst Apps login.",
    };
  };

  const handleQburstAppsLoginError = (error: any): UserLoginResponse => {
    // Handle 403 - not a registered user in databoard (QBurst Apps domain only)
    if (error.response?.status === 403 && isAccessedFromQBurstApps()) {
      const redirectUrl = error.response?.data?.redirect_url ?? "";
      setUnregisteredUserRedirectUrl(redirectUrl);
      setUnregisteredUserDialogOpen(true);
      return {
        isLoggedIn: false,
        errorMessage: "Not a registered user in QB Databoard.",
      };
    }

    // Handle 401 with redirect_url in error response
    if (error.response?.status === 401 && error.response?.data?.redirect_url) {
      if (isAccessedFromQBurstApps()) {
        console.log(
          "401 Unauthorized - Redirecting to:",
          error.response.data.redirect_url,
        );
        globalThis.window.location.href = error.response.data.redirect_url;
        return {
          isLoggedIn: false,
          errorMessage: "Redirecting to QBurst Apps login...",
        };
      }
    }
    return {
      isLoggedIn: false,
      errorMessage:
        error.response?.data?.message ?? "QBurst Apps login failed.",
    };
  };

  const qburstAppsLogin = async (
    maxRetries: number = 2,
  ): Promise<UserLoginResponse> => {
    setIsQBurstAppsLoggingIn(true);
    let retryCount = 0;
    try {
      while (retryCount < maxRetries) {
        try {
          const response: AxiosResponse = await attemptQBurstAppsLogin();
          console.log("QBurst Apps login response:", response);
          const result = processQBurstAppsLoginResponse(response);
          console.log("result", result);
          if (result === null) {
            console.log("result is null");
            retryCount++;
            if (retryCount < maxRetries) {
              await waitBeforeRetry(retryCount);
              console.log("retryCount", retryCount);
              continue;
            }
            return {
              isLoggedIn: false,
              errorMessage: "QBurst Apps authentication failed after retries.",
            };
          }

          return result;
        } catch (error: any) {
          console.error("QBurst Apps login error:", error);
         return handleQburstAppsLoginError(error);
        }
      }

      return {
        isLoggedIn: false,
        errorMessage: "QBurst Apps authentication failed after retries.",
      };
    } finally {
      setIsQBurstAppsLoggingIn(false);
    }
  };

  const userLogin = async (tokenResponse: TokenResponse) => {
    try {
      // Define the request body
      const requestBody = {
        access_token: tokenResponse.access_token,
      };

      // Make a POST request to the login API
      const response: AxiosResponse = await axios({
        method: "post",
        url: `${baseUrl}/login/`,
        data: requestBody,
        headers: {
          "Content-Type": "application/json",
        },
      });

      // Check if the response status is OK (200)
      if (response.status === 200 && response.data !== "Unauthorized") {
        // Handle successful login here, e.g., store authentication token, redirect, etc.
        console.log("Login successful");
        // set user token context
        setToken(response.data.access);

        const userDetails: UserDetails = {
          access_token: response.data.access,
          refresh_token: response.data.refresh,
          email: response.data.email,
          name: response.data.name,
          profile_pic: response.data.profile_image,
          id: response.data.id,
          roles: response.data.roles,
          has_primary: response.data.has_primary_dashboard,
          virtual_team: response.data.virtual_team,
          has_ceo_roles_access: false,
        };
        // set user details to localstorage
        setLocalUserData(userDetails);
      } else {
        console.error("User not logged in");
        return {
          isLoggedIn: false,
          errorMessage: "Login failed. Please check your credentials.",
        };
      }

      // Reset form fields and error message
      return {
        isLoggedIn: true,
        hasPrimary: response.data.has_primary_dashboard,
      };
    } catch (error: any) {
      // Handle login error
      console.error("Login error:", error);
      // Not a registered user
      if (error.response.status === 403) {
        return {
          isLoggedIn: false,
          errorMessage: "Not a registered user. Please contact administrator.",
        };
      } else if (error.response.status === 401) {
        return {
          isLoggedIn: false,
          errorMessage: "Failed to fetch user info. Please try again.",
        };
      } else {
        // Display an error message to the user
        return {
          isLoggedIn: false,
          errorMessage: "Unexpected Error. Please contact administrator.",
        };
      }
    }
  };

  useEffect(() => {
    console.log("initial rendering");
    const handleQBurstAppsLogin = async () => {
      if (!isAccessedFromQBurstApps()) return;

      const hasAccessToken = localUserData?.access_token;
      const hasRefreshToken = localUserData?.refresh_token;

      if (!hasAccessToken && !hasRefreshToken) {
        console.log(
          "Accessing from QBurst Apps domain, calling QBurst Apps login...",
        );
        await qburstAppsLogin();
      }
    };

    handleQBurstAppsLogin();
  }, []);

  useEffect(() => {
    if (!document.referrer) return;

    const parentOrigin = new URL(document.referrer).origin;
    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== parentOrigin) return;

      if (event.data.type === "guestToken") {
        const _guestToken = event.data.guestToken;
        if (typeof _guestToken !== "string")
          throw new Error("guestToken should be a string");
        setLocalGuestToken(_guestToken);
        setGuestToken(_guestToken);
      }
    };

    window.addEventListener("message", handleMessage);

    return () => {
      setLocalGuestToken(null);
      setGuestToken(null);
      window.removeEventListener("message", handleMessage);
    };
    // eslint-disable-next-line
  }, []);

  const handleUnregisteredUserDialogClose = () => {
    setUnregisteredUserDialogOpen(false);
    setUnregisteredUserRedirectUrl(null);
  };

  const contextData = useMemo(
    () => ({
      user: localUserData,
      accessToken,
      guestToken,
      userRoles: localUserData?.roles ?? [],
      hasCeoRolesAccess: localUserData?.has_ceo_roles_access ?? false,
      isQBurstAppsLoggingIn,
      unregisteredUserDialogOpen,
      unregisteredUserRedirectUrl,
      handleUnregisteredUserDialogClose,
      userLogin,
      setToken,
      clearUser,
      setUser: setLocalUserData,
    }),
    [
      localUserData,
      accessToken,
      guestToken,
      isQBurstAppsLoggingIn,
      unregisteredUserDialogOpen,
      unregisteredUserRedirectUrl,
      userLogin,
      setLocalUserData,
    ],
  );
  return (
    <GoogleAuthContext.Provider value={contextData}>
      {children}
    </GoogleAuthContext.Provider>
  );
};
